import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import "./DropshippingSweepstakes.css";

const faqSchema = {
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Low dropshipping sales? Can a sweepstakes help?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Yes. A sweepstakes can drive engagement, increase repeat visits, and encourage larger carts by making entries visible across the storefront.",
      },
    },
    {
      "@type": "Question",
      name: "How do I run a dropshipping sweepstakes?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Define the rules, configure entry bonuses, publish storefront widgets, and manage entrants and winners with a Shopify sweepstakes app.",
      },
    },
    {
      "@type": "Question",
      name: "Do sweepstakes require AMOE compliance?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Many regions require a free Alternate Method of Entry. Clean Sweeps supports AMOE workflows, but confirm requirements with legal counsel.",
      },
    },
  ],
};

const DropshippingSweepstakes = () => {
  return (
    <main className="dropshipping-page">
      <Seo
        title="Dropshipping Sweepstakes | Boost Low Sales"
        description="Low dropshipping sales? Run a sweepstakes with Clean Sweeps, a Shopify sweepstakes platform and app to manage entries, engagement, and compliance."
        path="/dropshipping-sweepstakes"
        image="/og/dropshipping-sweepstakes.png"
        imageAlt="Dropshipping sweepstakes guide"
        schema={faqSchema}
      />

      <header className="dropshipping-page__hero">
        <p className="dropshipping-page__eyebrow">Dropshipping sweepstakes</p>
        <h1>Low dropshipping sales? Run a sweepstakes.</h1>
        <p className="dropshipping-page__lead">
          Clean Sweeps helps dropshipping stores run and manage sweepstakes with
          a Shopify sweepstakes app, purchase-based entries, and storefront
          widgets that keep shoppers engaged.
        </p>
        <div className="dropshipping-page__actions">
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Install Clean Sweeps
          </a>
          <Link to="/resources" className="btn btn--ghost">
            Browse resources
          </Link>
        </div>
      </header>

      <section className="dropshipping-page__section">
        <div className="dropshipping-page__header">
          <h2>Why sweepstakes boost dropshipping engagement.</h2>
          <p>
            Sweepstakes give dropshipping shoppers a reason to buy now and come
            back to see their progress.
          </p>
        </div>
        <div className="dropshipping-page__grid">
          <article className="dropshipping-page__card">
            <h3>Higher cart values</h3>
            <p>
              Bonus entries and multipliers motivate shoppers to add more items
              and reach entry thresholds.
            </p>
          </article>
          <article className="dropshipping-page__card">
            <h3>Repeat visits</h3>
            <p>
              Countdown bonuses and visible entry totals bring customers back
              during the sweepstakes period.
            </p>
          </article>
          <article className="dropshipping-page__card">
            <h3>Clear value</h3>
            <p>
              Storefront widgets show entries at every step, making the reward
              obvious for each purchase.
            </p>
          </article>
        </div>
      </section>

      <section className="dropshipping-page__section">
        <div className="dropshipping-page__header">
          <h2>How to run a dropshipping sweepstakes.</h2>
          <p>Follow these steps to launch and manage sweepstakes smoothly.</p>
        </div>
        <ol className="dropshipping-page__steps">
          <li>
            Define the prize, official rules, eligibility, and AMOE options for
            your sweepstakes.
          </li>
          <li>
            Configure entry rules, bonuses, and product eligibility in Clean
            Sweeps.
          </li>
          <li>
            Publish widgets on product, cart, checkout, and thank-you pages to
            show entries everywhere.
          </li>
          <li>
            Manage entrants, select winners, and export records for reporting.
          </li>
        </ol>
      </section>

      <section className="dropshipping-page__section">
        <div className="dropshipping-page__header">
          <h2>Messaging for low dropshipping sales.</h2>
          <p>
            Highlight entries and sweepstakes value in your merchandising and
            email flows.
          </p>
        </div>
        <div className="dropshipping-page__grid">
          <article className="dropshipping-page__card">
            <h3>Product page callout</h3>
            <p>"Buy now to earn sweepstakes entries with this item."</p>
          </article>
          <article className="dropshipping-page__card">
            <h3>Cart reminder</h3>
            <p>"Add $20 more for 50 bonus entries."</p>
          </article>
          <article className="dropshipping-page__card">
            <h3>Post-purchase follow-up</h3>
            <p>"You earned 120 entries. See your totals in your account."</p>
          </article>
        </div>
      </section>

      <section className="dropshipping-page__section dropshipping-page__faq">
        <div className="dropshipping-page__header">
          <h2>Dropshipping sweepstakes FAQs</h2>
          <p>Answers for low dropshipping sales and engagement challenges.</p>
        </div>
        <div className="dropshipping-page__faq-item">
          <h3>Can sweepstakes improve low dropshipping sales?</h3>
          <p>
            Yes. Sweepstakes create urgency and visibility, encouraging shoppers
            to add more items and return to track their entries.
          </p>
        </div>
        <div className="dropshipping-page__faq-item">
          <h3>Is Clean Sweeps a Shopify sweepstakes app?</h3>
          <p>
            Clean Sweeps is a Shopify sweepstakes app and platform built for
            purchase-based entries, referrals, and widgets.
          </p>
        </div>
        <div className="dropshipping-page__faq-item">
          <h3>What about AMOE compliance?</h3>
          <p>
            Use an Alternate Method of Entry where required and confirm
            compliance with legal counsel.
          </p>
        </div>
      </section>

      <section className="dropshipping-page__cta">
        <h2>Turn dropshipping traffic into sweepstakes entries.</h2>
        <p>
          Run a sweepstakes that keeps shoppers engaged from product page to
          checkout.
        </p>
        <div className="dropshipping-page__actions">
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Get the Shopify app
          </a>
          <Link to="/how-to-run-a-sweepstakes" className="btn btn--ghost">
            Read the guide
          </Link>
        </div>
      </section>
    </main>
  );
};

export default DropshippingSweepstakes;
